import type { BotColorDef } from "@rakazo/core";
import {
  ACTIVE_RUN_STATUSES,
  avatarIdentitySeed,
  BOT_AVATAR_COLORS,
  BOT_COLOR_LIST,
  DEFAULT_BOT_AVATAR_COLOR,
  organicAvatarPath,
  resolvePersonaColorDef,
  robotAvatarFaceLayout,
  SHIPPED_BOT_AVATAR_DEFAULT_SHAPE_COUNT,
  SHIPPED_BOT_AVATAR_SHAPE_KEYS,
  SHIPPED_BOT_AVATAR_SHAPES,
  SHIPPED_BOT_AVATAR_VIEWBOX,
  shippedBotAvatarShapePath,
  shippedHash,
} from "@rakazo/core";
import { tokens } from "@rakazo/ui-tokens";
import type { CSSProperties } from "react";
import { memo, useId, useMemo, useSyncExternalStore } from "react";
import type { AvatarStyle } from "./avatar-style.js";
import { useAvatarStyle } from "./avatar-style.js";
import { cn } from "./lib/utils.js";
import "./styles.css";

export type { BotColorDef };
export { BOT_AVATAR_COLORS, BOT_COLOR_LIST, DEFAULT_BOT_AVATAR_COLOR, resolvePersonaColorDef };

export const BOT_SHAPES = SHIPPED_BOT_AVATAR_SHAPES;
export const SHIPPED_SHAPE_KEYS = SHIPPED_BOT_AVATAR_SHAPE_KEYS;
const VIEWBOX = SHIPPED_BOT_AVATAR_VIEWBOX;

export const BOT_MASCOT_SHAPES = SHIPPED_SHAPE_KEYS.map(
  (k) => BOT_SHAPES[k] ?? FALLBACK_SHAPE_PATH,
);

const FALLBACK_SHAPE_PATH = BOT_SHAPES.hex ?? "";

export function resolvePersonaShape(identity: string, explicitShape?: string | null): string {
  if (explicitShape) {
    const explicit = BOT_SHAPES[explicitShape];
    if (explicit) return explicit;
  }
  let hash = shippedHash(identity);
  hash = Math.imul(hash ^ (hash >>> 16), 73244475);
  hash = Math.imul(hash ^ (hash >>> 13), 3266489909);
  const shapeIndex = ((hash ^ (hash >>> 16)) >>> 0) % SHIPPED_BOT_AVATAR_DEFAULT_SHAPE_COUNT;
  const key = SHIPPED_SHAPE_KEYS[shapeIndex] ?? "hex";
  return BOT_SHAPES[key] ?? FALLBACK_SHAPE_PATH;
}

export function parseBotAvatar(
  rawColor: string,
  _identity?: string,
): {
  color: string;
  shapeIndex?: number;
  isImage: boolean;
  imageUrl?: string;
} {
  if (!rawColor) return { color: "#F97316", isImage: false };
  // Only data: image URLs are rendered. Arbitrary http(s)/blob values in `color`
  // must not become <img src> (SSRF / tracking when other members view the bot).
  if (rawColor.startsWith("data:image/")) {
    return { color: "#F97316", isImage: true, imageUrl: rawColor };
  }
  if (rawColor.includes("::shape_")) {
    const parts = rawColor.split("::shape_");
    const rawShapeIdx = parts[1] ?? "0";
    const parsedShapeIdx = /^\d+$/.test(rawShapeIdx) ? Number(rawShapeIdx) : 0;
    const shapeIdx = Number.isSafeInteger(parsedShapeIdx) ? parsedShapeIdx : 0;
    return {
      color: parts[0] || "#F97316",
      shapeIndex: shapeIdx % SHIPPED_SHAPE_KEYS.length,
      isImage: false,
    };
  }
  return { color: rawColor, isImage: false };
}

const AVATAR_HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function defaultBotAvatarValue(rawColor: string): string {
  const parsed = parseBotAvatar(rawColor);
  if (rawColor && !parsed.isImage && AVATAR_HEX.test(parsed.color)) return parsed.color;
  return DEFAULT_BOT_AVATAR_COLOR;
}

export interface BotAvatarProps {
  color: string;
  size?: number;
  status?: string;
  identity?: string;
  className?: string;
  variant?: AvatarStyle;
  /** The bot only coordinates: a small mark sits on the avatar, named by this text. */
  coordinatorLabel?: string;
}

/** The mark on a coordinator's avatar: three nodes joined, one handing to the others. */
function CoordinatorMark({ size, label }: { size: number; label: string }) {
  const mark = Math.max(12, Math.round(size * 0.38));
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className="absolute -bottom-0.5 -end-0.5 z-20 grid place-items-center rounded-full bg-primary text-primary-foreground ring-2 ring-background"
      style={{ width: mark, height: mark }}
    >
      <svg
        width={Math.round(mark * 0.66)}
        height={Math.round(mark * 0.66)}
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <circle cx="6" cy="2.6" r="1.4" fill="currentColor" stroke="none" />
        <circle cx="2.6" cy="9.2" r="1.4" fill="currentColor" stroke="none" />
        <circle cx="9.4" cy="9.2" r="1.4" fill="currentColor" stroke="none" />
        <path d="M6 4v1.8M6 5.8 3.2 7.8M6 5.8l2.8 2" />
      </svg>
    </span>
  );
}

export const BotAvatar = memo(function BotAvatar({ coordinatorLabel, ...props }: BotAvatarProps) {
  if (!coordinatorLabel) return <BotAvatarBase {...props} />;
  const size = props.size ?? 36;
  return (
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
      <BotAvatarBase {...props} />
      <CoordinatorMark size={size} label={coordinatorLabel} />
    </span>
  );
});

const BotAvatarBase = memo(function BotAvatarBase({
  color,
  size = 36,
  status,
  identity = "",
  className,
  variant,
}: BotAvatarProps) {
  const id = useId().replace(/[^a-zA-Z0-9-_]/g, "");
  const isWorking = ACTIVE_RUN_STATUSES.some((s) => s === status);
  const preferredVariant = useAvatarStyle();

  const parsed = useMemo(() => parseBotAvatar(color, identity), [color, identity]);
  const effectiveId = identity || parsed.color || "agent";

  const colorDef = useMemo(
    () => resolvePersonaColorDef(effectiveId, parsed.color),
    [effectiveId, parsed.color],
  );

  if (parsed.isImage && parsed.imageUrl) {
    return (
      <div
        className={cn("rakazo-bot-avatar relative shrink-0 select-none", className)}
        data-working={isWorking}
        style={{ width: size, height: size }}
      >
        <div className="rakazo-bot-avatar-picture relative flex h-full w-full items-center justify-center overflow-hidden rounded-full border border-border bg-secondary">
          <img src={parsed.imageUrl} alt="" className="h-full w-full object-cover" />
        </div>
        {isWorking ? (
          <svg
            className="rakazo-bot-avatar-ring rakazo-bot-avatar-ring-working absolute z-10 pointer-events-none"
            style={{
              inset: -4,
              width: size + 8,
              height: size + 8,
            }}
            viewBox="0 0 48 48"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="24"
              cy="24"
              r="22"
              className="stroke-status-working"
              strokeWidth="3.2"
              strokeLinecap="round"
              strokeDasharray="45 80"
            />
          </svg>
        ) : null}
      </div>
    );
  }

  if ((variant ?? preferredVariant) === "organic") {
    return (
      <OrganicAvatar
        color={colorDef.hex}
        identity={effectiveId}
        size={size}
        isWorking={isWorking}
        className={className}
      />
    );
  }

  return (
    <RobotAvatar
      colorDef={colorDef}
      size={size}
      isWorking={isWorking}
      className={className}
      identity={effectiveId}
      gradientId={id}
      shapeIndex={parsed.shapeIndex}
    />
  );
});

function robotEyeAnimation(identity: string, mode: "idle" | "working"): CSSProperties {
  const seed = avatarIdentitySeed(identity);
  return {
    "--rakazo-eye-animation-name":
      mode === "idle" ? `rakazo-eyes-idle-${seed % 4}` : "rakazo-eyes-working",
    "--rakazo-eye-animation-duration":
      mode === "idle" ? `${(4.2 + ((seed * 7) % 28) / 10).toFixed(2)}s` : "1.4s",
    "--rakazo-eye-animation-easing":
      mode === "idle" ? "cubic-bezier(0.4, 0, 0.2, 1)" : "ease-in-out",
    "--rakazo-eye-animation-delay":
      mode === "idle" ? `${(-(((seed * 13) % 45) / 10)).toFixed(2)}s` : "0s",
  } as CSSProperties;
}

function RobotAvatar({
  colorDef,
  size,
  isWorking,
  className,
  identity,
  gradientId,
  shapeIndex,
}: {
  colorDef: BotColorDef;
  size: number;
  isWorking: boolean;
  className?: string;
  identity: string;
  gradientId: string;
  shapeIndex?: number;
}) {
  const { visorW, visorH, eyeW, eyeH, eyeGap, eyeRadius, offsetY } = robotAvatarFaceLayout(
    size,
    shapeIndex,
  );
  const shapePath = shapeIndex === undefined ? undefined : shippedBotAvatarShapePath(shapeIndex);
  const eyeGlow = `0 0 4px #fff, 0 0 8px #fff, 0 0 14px ${colorDef.light}`;

  return (
    <div
      className={cn(
        "rakazo-bot-avatar relative inline-flex shrink-0 items-center justify-center select-none",
        !shapePath && "rounded-full",
        className,
      )}
      data-working={isWorking}
      style={{
        width: size,
        height: size,
        background: shapePath
          ? undefined
          : `radial-gradient(circle at 35% 26%, ${colorDef.light}, ${colorDef.hex} 55%, ${colorDef.dark} 100%)`,
        boxShadow: shapePath
          ? undefined
          : isWorking
            ? size > 40
              ? `0 0 0 2px rgba(255,255,255,0.25), 0 0 ${Math.round(size * 0.45)}px ${colorDef.hex}`
              : "0 0 0 2px rgba(255,255,255,0.25)"
            : `0 2px ${Math.max(4, Math.round(size * 0.15))}px rgba(0,0,0,0.4)`,
      }}
    >
      <svg
        className="rakazo-bot-avatar-ring overflow-visible pointer-events-none absolute"
        style={{
          inset: -4,
          width: size + 8,
          height: size + 8,
          filter:
            size > 40
              ? `drop-shadow(0 0 6px ${colorDef.light}) drop-shadow(0 0 10px #ffffff)`
              : undefined,
        }}
        viewBox="0 0 48 48"
        fill="none"
        aria-hidden="true"
      >
        <circle
          cx="24"
          cy="24"
          r="22"
          stroke={`url(#${gradientId}-ring)`}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeDasharray="45 80"
        />
        <circle cx="43" cy="24" r="2.8" fill="#ffffff" />
        <defs>
          <linearGradient id={`${gradientId}-ring`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="60%" stopColor={colorDef.light} stopOpacity="0.9" />
            <stop offset="100%" stopColor={colorDef.light} stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      {shapePath ? (
        <svg
          viewBox={VIEWBOX}
          width={size}
          height={size}
          aria-hidden="true"
          className="rakazo-bot-avatar-body absolute inset-0 overflow-visible"
          style={{
            filter: isWorking
              ? size > 40
                ? `drop-shadow(0 0 8px ${colorDef.light})`
                : undefined
              : "drop-shadow(0 2px 4px rgba(0,0,0,0.45))",
          }}
        >
          <defs>
            <radialGradient id={`${gradientId}-body`} cx="35%" cy="26%" r="85%">
              <stop offset="0%" stopColor={colorDef.light} />
              <stop offset="55%" stopColor={colorDef.hex} />
              <stop offset="100%" stopColor={colorDef.dark} />
            </radialGradient>
          </defs>
          <path d={shapePath} fill={`url(#${gradientId}-body)`} />
        </svg>
      ) : null}
      <div
        className="rakazo-bot-avatar-visor relative flex items-center justify-center overflow-hidden"
        style={{
          width: visorW,
          height: visorH,
          top: offsetY,
          boxSizing: "border-box",
          borderRadius: Math.round(visorH * 0.52),
          background: "linear-gradient(180deg, #101014 0%, #030305 100%)",
          boxShadow: "inset 0 1.5px 3px rgba(0,0,0,0.95), 0 1px 1px rgba(255,255,255,0.18)",
          border: "1px solid rgba(255,255,255,0.14)",
        }}
      >
        {(["idle", "working"] as const).map((mode) => (
          <div
            key={mode}
            className={`rakazo-bot-avatar-eyes rakazo-bot-avatar-eyes-${mode} absolute inset-0 z-10 flex items-center justify-center`}
            style={{
              gap: eyeGap,
              ...robotEyeAnimation(identity || colorDef.hex, mode),
            }}
          >
            {[0, 1].map((eye) => (
              <span
                key={eye}
                className="block"
                style={{
                  width: eyeW,
                  height: eyeH,
                  borderRadius: eyeRadius,
                  backgroundColor: "#fff",
                  boxShadow: eyeGlow,
                }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function OrganicAvatar({
  color,
  identity,
  size,
  isWorking,
  className,
}: {
  color: string;
  identity?: string;
  size: number;
  isWorking: boolean;
  className?: string;
}) {
  const reducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    reducedMotionSnapshot,
    () => false,
  );
  const seed = avatarIdentitySeed(identity || color || DEFAULT_BOT_AVATAR_COLOR);
  const duration = `${4.8 + (seed % 24) / 10}s`;
  const shapeA = organicAvatarPath(seed);
  const shapeB = organicAvatarPath(seed, 0.42);

  return (
    <svg
      viewBox="-60 -60 120 120"
      aria-hidden="true"
      className={cn("rakazo-organic-avatar overflow-visible select-none", className)}
      data-working={isWorking}
      data-shape-family={seed % 10}
      data-eye-pattern={seed % 4}
      style={{
        width: size,
        height: size,
        flex: "none",
      }}
    >
      {(["idle", "working"] as const).map((mode) => (
        <path
          key={mode}
          className={`rakazo-organic-avatar-body rakazo-organic-avatar-body-${mode}`}
          d={shapeA}
          fill={color}
          style={
            {
              "--rakazo-organic-path": `path("${shapeA}")`,
              filter:
                mode === "working"
                  ? `drop-shadow(0 0 ${Math.round(size * 0.16)}px ${color})`
                  : "drop-shadow(0 2px 3px rgba(0,0,0,.34))",
            } as CSSProperties
          }
        >
          {!reducedMotion ? (
            <animate
              attributeName="d"
              values={`${shapeA};${shapeB};${shapeA}`}
              dur={duration}
              repeatCount="indefinite"
            />
          ) : null}
        </path>
      ))}
      <g transform={`rotate(${(seed % 9) - 4})`}>
        {(["idle", "working"] as const).map((mode) => (
          <g
            key={mode}
            className={`rakazo-organic-avatar-eyes rakazo-organic-avatar-eyes-${mode}`}
            fill={tokens.background}
          >
            <rect x="-14" y="-12" width="7" height="24" rx="3.5" />
            <rect x="7" y="-12" width="7" height="24" rx="3.5" />
          </g>
        ))}
      </g>
    </svg>
  );
}

const reducedMotionMedia = "(prefers-reduced-motion: reduce)";

function reducedMotionSnapshot(): boolean {
  return window.matchMedia(reducedMotionMedia).matches;
}

function subscribeToReducedMotion(onChange: () => void): () => void {
  const media = window.matchMedia(reducedMotionMedia);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}

export function BotShapePreview({
  shapeIndex,
  color,
  selected,
  onClick,
}: {
  shapeIndex: number;
  color: string;
  selected?: boolean;
  onClick?: () => void;
}) {
  const key = SHIPPED_SHAPE_KEYS[shapeIndex % SHIPPED_SHAPE_KEYS.length] ?? "hex";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={key}
      aria-pressed={selected ?? false}
      className={cn(
        "relative flex size-11 items-center justify-center rounded-xl transition-transform hover:scale-105 motion-safe:active:scale-[0.96] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover",
        selected
          ? "ring-2 ring-primary ring-offset-2 ring-offset-popover bg-white/10"
          : "hover:bg-white/5",
      )}
    >
      <BotAvatar
        color={`${color}::shape_${shapeIndex}`}
        identity="preview"
        variant="robot"
        size={32}
      />
    </button>
  );
}

export function Wordmark({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div className="flex h-11 w-11 items-center justify-center gap-1.5 rounded-full bg-card">
        <span className="h-4 w-[7px] rounded-full bg-primary" />
        <span className="h-4 w-[7px] rounded-full bg-primary" />
      </div>
      <span className="font-[Aeonik,ui-sans-serif] text-[28px] tracking-tight text-foreground">
        HrokBot
      </span>
    </div>
  );
}
